/**
 * Pure validator for the Invariant Lifecycle Governance demo.
 *
 * Mirrors the deterministic, fail-closed checks of the ILG Validator
 * Rulebook v0 (`theory/manuscripts/contrib/InvariantLifecycleGovernance/
 * reference/Validator-Rulebook.md`) for the subset of artifact types the
 * demo exercises: PreservationWitness, EstablishReceipt, ProofArtifact,
 * and the IssuerCapability scope check.
 *
 * Each accept emits a `GovArrow`-shaped accept verdict; each refuse emits
 * exactly one primary `ViolationReceipt` with a stable code. Rule order
 * matches the rulebook's primary-firing precedence so the demo's verdict
 * is the same code the rulebook would assign.
 *
 * SPDX-License-Identifier: LicenseRef-Proprietary
 * Copyright (c) 2026 Invariant Holdings LLC
 */

export type Kind = "Algebraic" | "ResourceBound";
export type AbsDiffClass = "equivalent" | "tightened" | "expanded" | "unknown";
export type DriftSeverity = "low" | "medium" | "high" | "unknown";

export interface PreservationWitnessInput {
  readonly invariant_id: string;
  readonly kind: Kind;
  readonly abs_diff_class: AbsDiffClass;
  readonly drift_severity: DriftSeverity;
  readonly drift_receipt_present: boolean;
  readonly issuer_id: string;
  readonly fingerprint_continuous: boolean;
  /**
   * Whether the witness carries positive preservation_evidence. When
   * `abs_diff_class ∈ {expanded, unknown}`, the schema requires the
   * evidence to be empty; non-empty evidence under those classes is a
   * structural violation flagged by the schema gate.
   */
  readonly evidence_present: boolean;
  /** R-type only — whether accounting semantics changed under this lift. */
  readonly accounting_drift?: boolean;
  /** R-type only — whether a MigrationArtifact accompanies the witness. */
  readonly mig_present?: boolean;
  /** R-type only — whether budget increased without explicit release authority. */
  readonly budget_increase_without_release?: boolean;
  /** Whether A-type evidence shows constructor drift without a proof transport. */
  readonly a_type_constructor_drift_without_transport?: boolean;
  /** Optional pre-state fingerprint (digest of the input state); used by chain composition. */
  readonly pre_fingerprint?: string;
  /** Optional post-state fingerprint (digest of the output state); used by chain composition. */
  readonly post_fingerprint?: string;
}

export interface IssuerCapabilityInput {
  readonly issuer_id: string;
  readonly may_sign_artifact_types: readonly string[];
  readonly invariant_ids: readonly string[];
  readonly kinds: readonly Kind[];
  readonly change_classes: readonly string[];
  /** Optional hard constraint: drift severity above this is refused. */
  readonly max_drift_severity?: DriftSeverity;
  /** Optional hard constraint: refuse witnesses with ABS_DIFF expanded/unknown. */
  readonly forbid_expanded_or_unknown_abs?: boolean;
  /** Whether the ICAP is currently revoked. */
  readonly revoked?: boolean;
  /** Whether the ICAP is past its expiration. */
  readonly expired?: boolean;
}

export interface EstablishmentInput {
  readonly kind: Kind;
  readonly bootstrap_present: boolean;
  readonly proof_present: boolean;
  readonly initial_budget_receipt_present?: boolean;
  readonly issuer_id: string;
  readonly hash_binds_match: boolean;
  readonly decl_kind_matches_proof_kind?: boolean;
}

export interface ProofArtifactInput {
  readonly kind: Kind;
  readonly budget_dependent: boolean;
  readonly constructor_indexed: boolean;
}

export interface AcceptVerdict {
  readonly outcome: "accept";
  readonly subject: string;
}

export interface RefuseVerdict {
  readonly outcome: "refuse";
  readonly subject: string;
  readonly rule: string;
  readonly title: string;
  readonly severity: "critical" | "high" | "medium";
  readonly explanation: string;
}

export type Verdict = AcceptVerdict | RefuseVerdict;

const SEVERITY_ORDER: Readonly<Record<DriftSeverity, number>> = {
  low: 0,
  medium: 1,
  high: 2,
  unknown: 3,
};

function exceeds(actual: DriftSeverity, cap: DriftSeverity): boolean {
  return SEVERITY_ORDER[actual] > SEVERITY_ORDER[cap];
}

function refuse(
  subject: string,
  rule: string,
  title: string,
  severity: RefuseVerdict["severity"],
  explanation: string,
): RefuseVerdict {
  return { outcome: "refuse", subject, rule, title, severity, explanation };
}

/**
 * Validate a PreservationWitness against an IssuerCapability.
 *
 * The check sequence matches the rulebook's primary-firing precedence:
 *   schema-side constraints (continuity, ABS, drift) before kind-specific
 *   resource checks; ICAP scope last (since a scope violation depends on
 *   the witness already being well-formed).
 */
export function validatePreservationWitness(
  witness: PreservationWitnessInput,
  icap: IssuerCapabilityInput,
): Verdict {
  const subject = `PreservationWitness:${witness.invariant_id}`;

  if (!witness.drift_receipt_present) {
    return refuse(
      subject,
      "ILG-P-001",
      "Missing DriftReceipt",
      "high",
      "A PreservationWitness must reference an explicit DriftReceipt; without one the witness cannot claim that drift has been characterized.",
    );
  }

  if (!witness.fingerprint_continuous) {
    return refuse(
      subject,
      "ILG-P-002",
      "Fingerprint continuity break",
      "critical",
      "pre_fingerprint does not match the last known state — this is a Preserve≠Re-establish laundering attempt (A3).",
    );
  }

  if (
    witness.abs_diff_class === "expanded" ||
    witness.abs_diff_class === "unknown"
  ) {
    if (witness.evidence_present) {
      return refuse(
        subject,
        "ILG-SG-001",
        "Unknown fields present",
        "high",
        "Schema constraint: when abs_diff_class ∈ {expanded, unknown}, preservation_evidence MUST be the empty object.",
      );
    }
    return refuse(
      subject,
      "ILG-P-004",
      "ABS diff expanded/unknown",
      "critical",
      `abs_diff_class = ${witness.abs_diff_class}; admission expansion is not bridgeable (Sᴬ has no bridge) and unknown is conservatively treated as expanded.`,
    );
  }

  if (
    witness.drift_severity === "high" ||
    witness.drift_severity === "unknown"
  ) {
    return refuse(
      subject,
      "ILG-P-005",
      "Drift severity high/unknown",
      "high",
      `Drift severity = ${witness.drift_severity}; preservation is refused until the drift is characterized as low or medium.`,
    );
  }

  if (witness.a_type_constructor_drift_without_transport === true) {
    return refuse(
      subject,
      "ILG-P-007",
      "A-type constructor drift without proof transport",
      "critical",
      "Constructor basis changed and no proof transport evidence accompanies the witness; the existing proof no longer types.",
    );
  }

  if (witness.kind === "ResourceBound") {
    if (witness.accounting_drift === true && witness.mig_present !== true) {
      return refuse(
        subject,
        "ILG-P-009",
        "R-type accounting drift without MIG",
        "critical",
        "Accounting semantics changed and no MigrationArtifact (MIG) is supplied; every prior R-type receipt is invalidated (A4 replay-window).",
      );
    }
    if (witness.budget_increase_without_release === true) {
      return refuse(
        subject,
        "ILG-P-010",
        "R-type monotonicity violation",
        "critical",
        "Budget increased without an explicit release authority artifact (A5 hidden replenishment).",
      );
    }
  }

  const icapVerdict = checkIssuerScope(witness, icap);
  if (icapVerdict !== null) {
    return icapVerdict;
  }

  return { outcome: "accept", subject };
}

/**
 * Validate an EstablishReceipt-shaped input. Schema-gate ordering: required
 * bindings, then kind match, then ICAP scope.
 */
export function validateEstablishment(
  establishment: EstablishmentInput,
  icap: IssuerCapabilityInput,
): Verdict {
  const subject = `EstablishReceipt:${establishment.kind}`;

  if (!establishment.bootstrap_present) {
    return refuse(
      subject,
      "ILG-E-001",
      "Missing BootstrapSpec",
      "critical",
      "EstablishReceipt references Decl + Proof but BootstrapSpec is absent or unverifiable.",
    );
  }
  if (!establishment.proof_present) {
    return refuse(
      subject,
      "ILG-E-002",
      "Missing ProofArtifact",
      "critical",
      "EstablishReceipt is missing its proof binding.",
    );
  }
  if (!establishment.hash_binds_match) {
    return refuse(
      subject,
      "ILG-E-003",
      "Hash-bind mismatch in EstablishReceipt",
      "critical",
      "Hashes in EstablishReceipt.binds do not match the referenced artifacts.",
    );
  }
  if (establishment.decl_kind_matches_proof_kind === false) {
    return refuse(
      subject,
      "ILG-E-004",
      "Kind mismatch Decl vs Proof",
      "critical",
      "Decl.kind ≠ Proof.kind for the same invariant_id.",
    );
  }
  if (
    establishment.kind === "ResourceBound" &&
    establishment.initial_budget_receipt_present !== true
  ) {
    return refuse(
      subject,
      "ILG-E-005",
      "R-type missing InitialBudgetReceipt binding",
      "critical",
      "kind = ResourceBound and EstablishReceipt.binds.initial_budget_receipt is missing.",
    );
  }
  if (!icap.may_sign_artifact_types.includes("EstablishReceipt")) {
    return refuse(
      subject,
      "ILG-E-006",
      "Issuer not verifiable (ICAP missing / out of scope / revoked / expired)",
      "critical",
      `ICAP ${icap.issuer_id} cannot sign EstablishReceipt artifacts.`,
    );
  }
  if (icap.revoked === true) {
    return refuse(
      subject,
      "ILG-E-006",
      "Issuer not verifiable (ICAP revoked)",
      "critical",
      `ICAP ${icap.issuer_id} is revoked; establishment refused.`,
    );
  }
  if (icap.expired === true) {
    return refuse(
      subject,
      "ILG-E-006",
      "Issuer not verifiable (ICAP expired)",
      "critical",
      `ICAP ${icap.issuer_id} is past its expiration; establishment refused.`,
    );
  }
  if (establishment.issuer_id !== icap.issuer_id) {
    return refuse(
      subject,
      "ILG-E-006",
      "Issuer not verifiable (ICAP id mismatch)",
      "critical",
      `EstablishReceipt issuer ${establishment.issuer_id} does not match ICAP issuer ${icap.issuer_id}.`,
    );
  }
  if (!icap.kinds.includes(establishment.kind)) {
    return refuse(
      subject,
      "ILG-E-006",
      "Issuer not verifiable (kind out of scope)",
      "critical",
      `ICAP ${icap.issuer_id} scope does not include kind ${establishment.kind}.`,
    );
  }

  return { outcome: "accept", subject };
}

/** Validate a ProofArtifact against the Kind Separation Lemma (L3). */
export function validateProofArtifact(proof: ProofArtifactInput): Verdict {
  const subject = `InvariantProofArtifact:${proof.kind}`;
  if (proof.kind === "Algebraic" && proof.budget_dependent) {
    return refuse(
      subject,
      "ILG-DR-003",
      "A-type proof budget dependence",
      "critical",
      "An Algebraic proof must not depend on budgets (Kind Separation Lemma, L3); this is the A6 smuggle pattern.",
    );
  }
  return { outcome: "accept", subject };
}

/**
 * Apply the ICAP scope discipline to a witness.
 *
 * Returns `null` when the scope check accepts; otherwise the precise
 * refusal. Composed by validatePreservationWitness.
 */
function checkIssuerScope(
  witness: PreservationWitnessInput,
  icap: IssuerCapabilityInput,
): RefuseVerdict | null {
  if (icap.revoked === true) {
    return refuse(
      `PreservationWitness:${witness.invariant_id}`,
      "ILG-P-006",
      "Issuer not verifiable (ICAP revoked)",
      "critical",
      `ICAP ${icap.issuer_id} is revoked; preservation witness refused.`,
    );
  }
  if (icap.expired === true) {
    return refuse(
      `PreservationWitness:${witness.invariant_id}`,
      "ILG-P-006",
      "Issuer not verifiable (ICAP expired)",
      "critical",
      `ICAP ${icap.issuer_id} is past its expiration; preservation witness refused.`,
    );
  }
  if (witness.issuer_id !== icap.issuer_id) {
    return refuse(
      `PreservationWitness:${witness.invariant_id}`,
      "ILG-P-006",
      "Issuer not verifiable (ICAP id mismatch)",
      "critical",
      `Witness issuer ${witness.issuer_id} does not match ICAP issuer ${icap.issuer_id}.`,
    );
  }
  if (!icap.may_sign_artifact_types.includes("PreservationWitness")) {
    return refuse(
      `PreservationWitness:${witness.invariant_id}`,
      "ILG-IC-001",
      "Scope eligibility failure (artifact type)",
      "critical",
      `ICAP ${icap.issuer_id} cannot sign PreservationWitness artifacts.`,
    );
  }
  if (!icap.invariant_ids.includes(witness.invariant_id)) {
    return refuse(
      `PreservationWitness:${witness.invariant_id}`,
      "ILG-IC-002",
      "Scope expansion without ICAPExpansion (invariant id)",
      "critical",
      `Witness targets ${witness.invariant_id}, outside ICAP scope ${icap.invariant_ids.join(", ")}.`,
    );
  }
  if (!icap.kinds.includes(witness.kind)) {
    return refuse(
      `PreservationWitness:${witness.invariant_id}`,
      "ILG-IC-002",
      "Scope expansion without ICAPExpansion (kind)",
      "critical",
      `Witness kind ${witness.kind} is outside ICAP scope ${icap.kinds.join(", ")}.`,
    );
  }
  if (
    icap.forbid_expanded_or_unknown_abs === true &&
    (witness.abs_diff_class === "expanded" ||
      witness.abs_diff_class === "unknown")
  ) {
    return refuse(
      `PreservationWitness:${witness.invariant_id}`,
      "ILG-IC-004",
      "Constraint bypass attempt (ABS_DIFF)",
      "critical",
      "Witness would require ABS_DIFF ∈ {expanded, unknown}; ICAP forbids it.",
    );
  }
  if (
    icap.max_drift_severity !== undefined &&
    exceeds(witness.drift_severity, icap.max_drift_severity)
  ) {
    return refuse(
      `PreservationWitness:${witness.invariant_id}`,
      "ILG-IC-004",
      "Constraint bypass attempt (drift severity)",
      "critical",
      `Witness drift severity ${witness.drift_severity} exceeds ICAP cap ${icap.max_drift_severity}.`,
    );
  }
  return null;
}
